// ConvoCX app shell — lightweight hash router (CON-853).
// Hash-based so the statically served PWA never needs server rewrites; each
// route carries an SEO document title + meta description (updated on nav).

import { useEffect, useMemo, useState } from 'react';

export interface ShellRoute {
  name: string;
  /** Hash path, e.g. '/inbox' (no leading '#'). */
  path: string;
  /** Document title suffix (without the "— ConvoCX" brand). */
  title: string;
  description: string;
}

export const DEFAULT_ROUTE = 'inbox';

export const ROUTES: ShellRoute[] = [
  { name: 'inbox', path: '/inbox', title: 'Inbox', description: 'Unified inbox — all social, chat, web and email conversations in one place.' },
  { name: 'contacts', path: '/contacts', title: 'Contacts & CRM', description: 'Contacts and CRM — people, organisations and deal context across every channel.' },
  { name: 'teams', path: '/teams', title: 'Teams', description: 'Teams and departments — manage your support team, roles and routing.' },
  { name: 'reports', path: '/reports', title: 'Reports', description: 'Reports and analytics — first response, resolution, volume and CSAT.' },
  { name: 'templates', path: '/templates', title: 'Message Templates', description: 'Message templates and canned responses for consistent replies.' },
  { name: 'canned', path: '/canned', title: 'Canned Responses', description: 'Canned responses — saved replies your team can drop into any conversation.' },
  { name: 'channels', path: '/channels', title: 'Channels', description: 'Channels and inboxes — connect WhatsApp, Messenger, Instagram, email, SMS and web chat.' },
  { name: 'labels', path: '/labels', title: 'Tags & Labels', description: 'Tags, labels and conversation organisation.' },
  { name: 'knowledge', path: '/knowledge', title: 'Knowledge Base', description: 'Knowledge base — shared answers and help-centre content.' },
  { name: 'csat', path: '/csat', title: 'CSAT', description: 'Customer satisfaction surveys and feedback.' },
  { name: 'notifications', path: '/notifications', title: 'Notifications', description: 'Notifications and activity across your workspace.' },
  { name: 'help', path: '/help', title: 'Help Centre', description: 'Help Centre and support resources.' },
  { name: 'settings', path: '/settings', title: 'Settings', description: 'Workspace settings — profile, security, business hours and billing.' },
  { name: 'billing', path: '/billing', title: 'Billing', description: 'Billing and plans.' },
];

export function routeByName(name: string): ShellRoute {
  return ROUTES.find((r) => r.name === name) ?? ROUTES[0];
}

function readPath(): string {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash || hash === '/') return `/${DEFAULT_ROUTE}`;
  return hash.startsWith('/') ? hash : `/${hash}`;
}

/** Current hash path + imperative navigation. */
export function useHashRoute(): { path: string; navigate: (to: string) => void } {
  const [path, setPath] = useState<string>(() => readPath());

  useEffect(() => {
    const onChange = () => setPath(readPath());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useMemo(() => (to: string) => {
    const next = to.startsWith('#') ? to : `#${to}`;
    if (window.location.hash !== next) {
      window.location.hash = next;
    } else {
      // Same hash — still sync state (e.g. re-clicking the active nav item).
      setPath(readPath());
    }
  }, []);

  return { path, navigate };
}